import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, CreditCard, Save, Shield, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";


const BANKS = [
  { code: "001", name: "Banco do Brasil" },
  { code: "033", name: "Santander" },
  { code: "104", name: "Caixa Econômica" },
  { code: "237", name: "Bradesco" },
  { code: "341", name: "Itaú" },
  { code: "356", name: "Banco Real" },
  { code: "389", name: "Banco Mercantil" },
  { code: "399", name: "HSBC" },
  { code: "422", name: "Safra" },
  { code: "453", name: "Banco Rural" },
  { code: "633", name: "Rendimento" },
  { code: "652", name: "Itaú Unibanco" },
  { code: "745", name: "Citibank" },
  { code: "756", name: "Sicoob" },
  { code: "077", name: "Inter" },
  { code: "260", name: "Nubank" },
  { code: "290", name: "PagSeguro" },
  { code: "380", name: "PicPay" },
  { code: "323", name: "Mercado Pago" },
  { code: "536", name: "Next" },
  { code: "336", name: "C6 Bank" },
  { code: "212", name: "Banco Original" },
  { code: "746", name: "Modal" },
  { code: "099", name: "Uniprime" }
];

export default function BankSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);
  const [formData, setFormData] = useState({
    bank_code: "",
    bank_name: "",
    agency: "",
    account_number: "",
    account_type: "checking",
    holder_name: "",
    holder_document: "",
    pix_key: "",
    pix_key_type: ""
  });

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const res = await fetch(`${API}/bank-accounts/my`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data) {
            setAccount(data);
            setFormData({
              bank_code: data.bank_code || "",
              bank_name: data.bank_name || "",
              agency: data.agency || "",
              account_number: data.account_number || "",
              account_type: data.account_type || "checking",
              holder_name: data.holder_name || "",
              holder_document: data.holder_document || "",
              pix_key: data.pix_key || "",
              pix_key_type: data.pix_key_type || ""
            });
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAccount();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBankSelect = (code) => {
    const bank = BANKS.find(b => b.code === code);
    setFormData(prev => ({
      ...prev,
      bank_code: code,
      bank_name: bank?.name || ""
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const res = await fetch(`${API}/bank-accounts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        setAccount(data);
        toast.success("Dados bancários salvos com sucesso!");
      } else {
        throw new Error('Erro ao salvar');
      }
    } catch (error) {
      toast.error("Erro ao salvar dados bancários");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Tem certeza que deseja remover seus dados bancários?")) return;

    try {
      const res = await fetch(`${API}/bank-accounts/my`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setAccount(null);
        setFormData({
          bank_code: "",
          bank_name: "",
          agency: "",
          account_number: "",
          account_type: "checking",
          holder_name: "",
          holder_document: "",
          pix_key: "",
          pix_key_type: ""
        });
        toast.success("Dados bancários removidos");
      }
    } catch (error) {
      toast.error("Erro ao remover dados");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#F3C9CF]/30 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-[#6E0F1A]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Building2 className="w-6 h-6 text-[#B3121B]" />
              <span className="font-serif text-xl text-[#6E0F1A]">Dados Bancários</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Info Card */}
        <Card className="card-dashboard mb-6 bg-[#567D50]/5 border-[#567D50]/20">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-[#567D50] mt-0.5" />
              <div>
                <p className="text-sm text-[#6E0F1A] font-medium">Receba seus presentes diretamente</p>
                <p className="text-xs text-[#6E0F1A]/70 mt-1">
                  Configure sua conta bancária para receber automaticamente os valores dos presentes. 
                  Nossa taxa é de apenas <strong>2,9%</strong> — a mais barata do mercado!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="font-serif text-xl text-[#6E0F1A]">
              Conta para Recebimento
            </CardTitle>
            <CardDescription>
              Os valores dos presentes serão transferidos para esta conta
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Bank Selection */}
              <div>
                <Label className="text-[#6E0F1A]">Banco *</Label>
                <Select value={formData.bank_code} onValueChange={handleBankSelect}>
                  <SelectTrigger className="input-wedding mt-2">
                    <SelectValue placeholder="Selecione o banco" />
                  </SelectTrigger>
                  <SelectContent>
                    {BANKS.map((bank) => (
                      <SelectItem key={bank.code} value={bank.code}>
                        {bank.code} - {bank.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Agency and Account */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="agency" className="text-[#6E0F1A]">Agência *</Label>
                  <Input
                    id="agency"
                    name="agency"
                    value={formData.agency}
                    onChange={handleChange}
                    placeholder="0000"
                    className="input-wedding mt-2"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="account_number" className="text-[#6E0F1A]">Conta *</Label>
                  <Input
                    id="account_number"
                    name="account_number"
                    value={formData.account_number}
                    onChange={handleChange}
                    placeholder="00000-0"
                    className="input-wedding mt-2"
                    required
                  />
                </div>
              </div>

              {/* Account Type */}
              <div>
                <Label className="text-[#6E0F1A]">Tipo de Conta *</Label>
                <Select 
                  value={formData.account_type} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, account_type: value }))}
                >
                  <SelectTrigger className="input-wedding mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Conta Corrente</SelectItem>
                    <SelectItem value="savings">Conta Poupança</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Holder Info */}
              <div>
                <Label htmlFor="holder_name" className="text-[#6E0F1A]">Nome do Titular *</Label>
                <Input
                  id="holder_name"
                  name="holder_name"
                  value={formData.holder_name}
                  onChange={handleChange}
                  placeholder="Nome completo"
                  className="input-wedding mt-2"
                  required
                />
              </div>

              <div>
                <Label htmlFor="holder_document" className="text-[#6E0F1A]">CPF/CNPJ do Titular *</Label>
                <Input
                  id="holder_document"
                  name="holder_document"
                  value={formData.holder_document}
                  onChange={handleChange}
                  placeholder="000.000.000-00"
                  className="input-wedding mt-2"
                  required
                />
              </div>

              {/* PIX Key (Optional) */}
              <div className="pt-4 border-t border-[#F3C9CF]/30">
                <h3 className="font-medium text-[#6E0F1A] mb-4">Chave PIX (Opcional)</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-[#6E0F1A]">Tipo da Chave</Label>
                    <Select 
                      value={formData.pix_key_type} 
                      onValueChange={(value) => setFormData(prev => ({ ...prev, pix_key_type: value }))}
                    >
                      <SelectTrigger className="input-wedding mt-2">
                        <SelectValue placeholder="Selecione" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cpf">CPF</SelectItem>
                        <SelectItem value="cnpj">CNPJ</SelectItem>
                        <SelectItem value="email">E-mail</SelectItem>
                        <SelectItem value="phone">Telefone</SelectItem>
                        <SelectItem value="random">Chave Aleatória</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="pix_key" className="text-[#6E0F1A]">Chave PIX</Label>
                    <Input
                      id="pix_key"
                      name="pix_key"
                      value={formData.pix_key}
                      onChange={handleChange}
                      placeholder="Sua chave PIX"
                      className="input-wedding mt-2"
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-4 pt-4">
                {account && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDelete}
                    className="border-red-500 text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Remover
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-[#B3121B] hover:bg-[#9A0F17] text-white rounded-full"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Salvando..." : "Salvar Dados Bancários"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
